import Fill from "ol/style/Fill";
import Stroke from "ol/style/Stroke";
import { Icon, Text as StyleText } from "ol/style";
import Style from "ol/style/Style";
import { FeatureLike } from "ol/Feature";
import { IFilterState } from "./interfaces";

const defaultPolygonStyle = new Style({
    fill: new Fill({
        color: "#F3D199"
    })
});

const defaultLineStyle = new Style({
    stroke: new Stroke({
        color: "#000000",
        width: 1
    }),
});

const filteredPolygonStyle = new Style({
    fill: new Fill({
        color: "#6699cc"
    })
});

const selectedPolygonStyle = new Style({
    fill: new Fill({
        color: "#95BF2E"
    })
});

const higlightedPolygonStyle = new Style({
    fill: new Fill({
        color: "#F3D19999"
    }),
    stroke: new Stroke({
        color: "#95BF2E",
        width: 3
    }),
});

const labelStyle = new Style({
    text: new StyleText({
        rotateWithView: true,
        overflow: true,
        fill: new Fill({
            color: "#000000aa"
        }) 
    })
});

const emptyStyle = new Style();

var filterState :IFilterState = {
    emptyGraves: false,
    noContract: false,
    projected: false,
    unpaidGraves: false,
    graveTypes: []
};

export function getLineStyle(f: FeatureLike, resolution: number) :Style {
    if (f.get("hrob") && resolution > 0.2) // ak 1m je < ako 5px
        return emptyStyle;
    return defaultLineStyle;
}

export function getPrintGraveStyle(f: FeatureLike, resolution: number) :Style {
    return getPolygonStyle(f, resolution, emptyStyle, true);
}

export function getLabelStyle(f: FeatureLike, resolution: number) :Style {
    let text = labelStyle.getText();
    let height = f.get("textHeight") / resolution;
    text.setFont(Math.round(height)+"px Arial");
    let position = f.get("textPosition");
    setTextPosition(position, text);
    text.setRotation(f.get("textAngle"));
    text.setText(f.get("labelText"));
    labelStyle.setText(text);
    return labelStyle;
}

export function getStandardPolygonStyle(f: FeatureLike, resolution: number) :Style {
    let defaultStyle = defaultPolygonStyle;
    if (filterState.projected && f.get("isProjected")) {
        defaultStyle = filteredPolygonStyle;
    } else if (filterState.emptyGraves && f.get("isEmpty")==1) {
        defaultStyle = filteredPolygonStyle;
    } else if (filterState.unpaidGraves && f.get("isUnpaid")==1) {
        defaultStyle = filteredPolygonStyle;
    } else if (filterState.noContract && typeof(f.get("hasContract")) === "undefined") {
        defaultStyle = filteredPolygonStyle;
    } else if (filterState.graveTypes.length > 0 && filterState.graveTypes.some(t => t === f.get("graveType"))) {
        defaultStyle = filteredPolygonStyle;
    }
    return getPolygonStyle(f, resolution, defaultStyle, true);
}

export function getSelectedPolygonStyle(f: FeatureLike, resolution: number) :Style {
    return getPolygonStyle(f, resolution, selectedPolygonStyle, false);
}

export function getHighlightedPolygonStyle(f: FeatureLike, resolution: number) :Style {
    return getPolygonStyle(f, resolution, higlightedPolygonStyle, false);
}

export function getOverviewMapStyle() :Style {
    return new Style({
        stroke: new Stroke({
            color: "#00000099"
        })
    })
}

export function getOrnamentStyle(f: FeatureLike, resolution: number) :Style {
    let typ = f.get("type");
    let folder = "", file = "";
    switch (typ) {
        case "K1":
            folder = "Kahan1/50_old/";
            break;
        case "K2":
            folder = "Kahan2/50_old/";
            break;
        case "S1":
            folder = "Sviecka/50_old/";
            break;
    }
    if (typ === "K1" || typ === "K2" || typ === "S1") {
        let startTime = f.get("startTime");
        let endTime = f.get("endTime");
        let currentTime = Date.now()/1000;
        let percentage = (currentTime - startTime)/(endTime - startTime);
        if (percentage < 0.2) {
            file = "o1.gif";
        } else if (percentage < 0.4) {
            file = "o2.gif";
        } else if (percentage < 0.6) {
            file = "o3.gif";
        } else if (percentage < 0.8) {
            file = "o4.gif";
        } else {
            file = "o5.gif";
        }
    }

    return new Style({
        image: new Icon({
            src: "./Ozdoby/"+folder+file
        })
    })
}

export function updateFilterState(newState:IFilterState) {
    filterState.emptyGraves = newState && newState.emptyGraves;
    filterState.noContract = newState && newState.noContract;
    filterState.projected = newState && newState.projected;
    filterState.unpaidGraves = newState && newState.unpaidGraves;
    filterState.graveTypes = newState.graveTypes;
}

function getPolygonStyle(f: FeatureLike, resolution: number, defaultStyle: Style, hideWhenZoomedOut: boolean) :Style {
    if (hideWhenZoomedOut && f.get("hrob") && resolution > 0.2) // ak 1m je < ako 5px
        return emptyStyle;
    
    if (f.get("hroby") && f.get("hroby").length > 0) {
        let text = f.get("hroby").join(",");
        if (resolution > 0.1)
            text = "";
        defaultStyle.setText(new StyleText({
            text: text,
            overflow: true
        }));
    } else
        defaultStyle.setText(new StyleText({ text: "" }));
    
    return defaultStyle;
}

function setTextPosition(vtxPosition: number, style: StyleText) {
    switch (vtxPosition) {
        case 1:
        case 2:
        case 3:
            style.setTextBaseline("bottom");
        break;
        case 4:
        case 5:
        case 6:
            style.setTextBaseline("top");
        break;
        case 7:
        case 8:
        case 9:
            style.setTextBaseline("middle");
        break;
    }
    switch (vtxPosition) {
        case 4:
        case 7:
        case 1:
            style.setTextAlign("left");
        break;
        case 5:
        case 8:
        case 2:
            style.setTextAlign("center");
        break;
        case 6:
        case 9:
        case 3:
            style.setTextAlign("right");
        break;
    }
}